import { divide, floor, get } from "lodash";

import { COLORS } from "@treelab/panda";

import { IChartFormValue } from "../types/form-type";
import { ColumnType, ITableColumn, ITableData, IWrokspaceData } from "../types/table-type";
import { ChartType } from "../types/global-type";

import BasicLineChart from "./BasicLineChart";
import {
  getBasicDataset,
  getXAxisData,
  getRealColumn,
  getXAxisLabelOptionByCategory,
  getOptionColorByValue,
  showUseOptionColorSwitch,
} from "../utils/option_main";
import { getGridWidth, checkGroupId } from "../utils/option_secondary";
import CalculationDataByChart from "./CalculationDataByChart";

import { SERIES_AXIS_LABEL_MIN_WIDTH, X_AXIS_WIDTH_RATIO_LABEL_COUNT } from "../constants/chart";
import { TABLE_CELL_VALUE_ICON_PENTAGRAM } from "../constants/tableData";
import { isDateType } from "../utils/tableData_secondary";
import { getColors } from "../utils/colors";

class LineChart extends BasicLineChart {
  public getDataset(
    formValue: IChartFormValue,
    tableData: ITableData,
    workspaceData: IWrokspaceData,
    calculation: CalculationDataByChart
  ) {
    const dataset = getBasicDataset(formValue, tableData, workspaceData, ChartType.BASIC_LINE, this.calculation);
    if (Array.isArray(dataset)) {
      const transformList = dataset
        .filter((item) => !!get(item, "transform"))
        .map((item) => get(item, "transform.config.value"));
      this.stackTypeList = transformList;
    } else {
      this.stackTypeList = [];
    }
    return dataset;
  }

  public getXAxis(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData) {
    const column = getRealColumn(formValue.dimensionId, tableData);
    const data = getXAxisData(get(this.datasetObj, "[0].source"));
    const basicConfig = {
      data: data,
      show: true,
      splitLine: {
        show: false,
      },
      axisLabel: {
        color: COLORS.grey,
        hideOverlap: true,
      },
    };
    const gridWidth = getGridWidth(
      this.getCanvasWidth(),
      this.isExpand,
      ChartType.BASIC_LINE,
      !!this.formValue.isShowYAxis,
      false
    );
    const splitNumber = floor(divide(gridWidth, X_AXIS_WIDTH_RATIO_LABEL_COUNT));
    if (!column) {
      return { type: "category", show: false };
    }
    if (isDateType(column.type)) {
      return {
        ...basicConfig,
        type: "time",
        show: true,
        axisLine: {
          show: true,
          lineStyle: {
            color: COLORS.greyLight1,
          },
        },
        axisLabel: {
          ...basicConfig.axisLabel,
          formatter: "{yyyy}/{M}/{d}",
        },
      };
    }
    if (column.type === ColumnType.NUMBER) {
      return {
        ...basicConfig,
        type: "value",
        splitNumber,
        boundaryGap: ["0%", "0%"],
        axisLine: {
          show: true,
          lineStyle: {
            color: COLORS.greyLight2,
          },
        },
      };
    }
    if (column.type === ColumnType.CURRENCY) {
      const {
        typeOptions: { currencyFormat },
      } = column as ITableColumn<ColumnType.CURRENCY>;
      return {
        ...basicConfig,
        type: "value",
        splitNumber,
        boundaryGap: ["0%", "0%"],
        name: currencyFormat,
        axisLine: {
          show: true,
          lineStyle: {
            color: COLORS.greyLight2,
          },
        },
      };
    }
    if (column.type === ColumnType.RATING) {
      return {
        ...basicConfig,
        type: "value",
        splitNumber,
        boundaryGap: ["0%", "0%"],
        name: TABLE_CELL_VALUE_ICON_PENTAGRAM,
        axisLine: {
          show: true,
          lineStyle: {
            color: COLORS.greyLight2,
          },
        },
      };
    }
    const isGlobalFormat = this.calculation.checkGlobalFormat();
    return {
      ...basicConfig,
      type: "category",
      boundaryGap: false,
      axisLine: {
        show: true,
        lineStyle: {
          color: COLORS.greyLight2,
        },
      },
      axisLabel: getXAxisLabelOptionByCategory(
        data,
        this.getCanvasWidth(),
        this.getCanvasHeight(),
        this.isExpand,
        !!this.formValue.isShowYAxis,
        isGlobalFormat ? this.formatDimension : undefined
      ),
    };
  }

  public getSeries(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData) {
    const { isSmoothLine, isShowLabel, chartType, isShowYAxis } = formValue;
    const xAxisWidth = getGridWidth(this.getCanvasWidth(), this.isExpand, chartType, !!isShowYAxis, true);
    const data = getXAxisData(get(this.datasetObj, "[0].source"));
    let isShowLable = true;
    if (xAxisWidth < data.length * SERIES_AXIS_LABEL_MIN_WIDTH) {
      isShowLable = false;
    }
    const basicConfig = {
      smooth: isSmoothLine ? 0.5 : 0,
      type: "line",
      symbolSize: 8,
      label: {
        overflow: "truncate",
        show: isShowLabel && isShowLable,
        formatter: (params: { data: any[] }) => {
          const value = params.data[1];
          return this.formatValue(value);
        },
      },
      animationDuration: 600,
      sampling: "lttb",
    };
    if (!this.stackTypeList.length) {
      return [
        {
          ...basicConfig,
          encode: { x: 0, y: 1 },
        },
      ];
    }

    let colorByOptionId: Record<string, string> = {};

    const useOptionColor =
      this.formValue.useOptionColor &&
      showUseOptionColorSwitch({ formValue: this.formValue, tableData: this.tableData });

    if (useOptionColor) {
      const groupId = checkGroupId(this.formValue, this.tableData, this.formValue.chartType);
      colorByOptionId = getOptionColorByValue({
        columns: tableData.columns,
        columnId: groupId || formValue.dimensionId,
      });
    }

    const seriesList = this.stackTypeList.map((item, index) => ({
      ...basicConfig,
      name: item,
      datasetIndex: index + 1,
      encode: { x: 0, y: 1 },
      itemStyle: useOptionColor
        ? {
          color: colorByOptionId[item] || getColors(index,item),
        }
        : null,
    }));

    return seriesList;
  }
}

export default LineChart;
